// /lib/api/apiError.ts

import axios, { AxiosError } from 'axios'

export type ApiErrorPayload = {
  code?: string
  /** The API's own business error code (e.g. `NO_AVAILABILITY`). */
  errorCode?: string
  errorMessage?: string
  message?: string
  errors?: ValidationError[]
  [key: string]: unknown
}

export type ValidationError = {
  key: string
  conditions: {
    type: string
    label?: string
    message: string
  }[]
}

export class ApiError extends Error {
  status?: number
  code?: string
  data?: ApiErrorPayload
  isNetworkError: boolean

  constructor(params: {
    message: string
    status?: number
    code?: string
    data?: ApiErrorPayload
    isNetworkError?: boolean
  }) {
    super(params.message)

    this.name = 'ApiError'
    this.status = params.status
    this.code = params.code
    this.data = params.data
    this.isNetworkError = !!params.isNetworkError
  }
}

/**
 * Converts unknown error -> ApiError
 * This should be used inside your Axios interceptor.
 */
export function toApiError(error: unknown): ApiError {
  // Axios error
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorPayload>

    const status = axiosError.response?.status
    const data = axiosError.response?.data

    return new ApiError({
      message: data?.message || axiosError.message || 'Unexpected API error',
      status,
      code: data?.code || axiosError.code,
      data,
      isNetworkError: !axiosError.response,
    })
  }

  // Non-Axios error
  if (error instanceof Error) {
    return new ApiError({
      message: error.message,
    })
  }

  return new ApiError({
    message: 'Unknown error occurred',
  })
}
